import React, { useState, useRef, useEffect } from 'react';
import DownArrow from '../../assets/down-arrow.png';

function AwardSizeFilter({ 
    appliedAwardSizeMin, 
    appliedAwardSizeMax, 
    onApply, 
    onReset 
}) {
    const [awardSizeMin, setAwardSizeMin] = useState(appliedAwardSizeMin);
    const [awardSizeMax, setAwardSizeMax] = useState(appliedAwardSizeMax);
    const [showAwardSizeDropdown, setShowAwardSizeDropdown] = useState(false);
    const awardSizeDropdownRef = useRef(null);

    // Update local state when props change
    useEffect(() => {
        setAwardSizeMin(appliedAwardSizeMin);
        setAwardSizeMax(appliedAwardSizeMax);
    }, [appliedAwardSizeMin, appliedAwardSizeMax]);

    // Helper function to format award size range for display
    const formatAwardSizeRange = (min, max) => {
        if (min === 0 && max === 1000000) {
            return "Award Size";
        }
        const formatAmount = (amount) => {
            if (amount >= 1000000) {
                return `$${(amount / 1000000).toFixed(1)}M`;
            } else if (amount >= 1000) {
                return `$${(amount / 1000).toFixed(0)}K`;
            } else {
                return `$${amount}`;
            }
        };
        return `Award Size: ${formatAmount(min)} - ${formatAmount(max)}`;
    };

    // Handle clicking outside award size dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (awardSizeDropdownRef.current && !awardSizeDropdownRef.current.contains(event.target)) {
                setShowAwardSizeDropdown(false);
            }
        };

        if (showAwardSizeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAwardSizeDropdown]);

    const handleApply = () => {
        onApply(awardSizeMin, awardSizeMax);
        setShowAwardSizeDropdown(false);
    };

    const handleReset = () => {
        setAwardSizeMin(0);
        setAwardSizeMax(1000000);
        onReset();
        setShowAwardSizeDropdown(false);
    };

    return (
        <div className="relative">
            <button 
                className={`px-4 py-2 rounded-md flex flex-row gap-2 items-center ${
                    appliedAwardSizeMin !== 0 || appliedAwardSizeMax !== 1000000
                        ? 'text-black shadow-sm border border-gray-500'
                        : 'border border-gray-500 hover:bg-gray-100 shadow-sm'
                }`}
                onClick={() => setShowAwardSizeDropdown(!showAwardSizeDropdown)}
            >
                {formatAwardSizeRange(appliedAwardSizeMin, appliedAwardSizeMax)}
                <img src={DownArrow} alt="Down Arrow" className='w-4 h-4' />
            </button>
            {showAwardSizeDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 z-10 min-w-80" ref={awardSizeDropdownRef}>
                    <h3 className="font-semibold mb-3">Award Size Range</h3>
                    
                    {/* Min Award Size */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Minimum: ${awardSizeMin.toLocaleString()}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="1000000"
                            step="10000"
                            value={awardSizeMin}
                            onChange={(e) => setAwardSizeMin(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    
                    {/* Max Award Size */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Maximum: ${awardSizeMax.toLocaleString()}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="1000000"
                            step="10000"
                            value={awardSizeMax}
                            onChange={(e) => setAwardSizeMax(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    
                    {/* Apply Button */}
                    <div className="flex justify-end gap-2">
                        <button
                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                        <button
                            className="px-3 py-1 text-sm bg-indigo-500 text-white rounded hover:bg-indigo-600"
                            onClick={handleApply}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AwardSizeFilter;